import { FrequencyTypeEnum, FrequencyStatusEnum, FrequencyCadenceTypeEnum } from '@generated';
import { convertDraftFrequenciesToCalendarEvent, convertFrequenciesToCalendarEvent } from '../util';
import { CertificationPeriodCalendar } from '~/features/scheduling/cert-periods/CertificationPeriodCalendar';
import { useEditFrequencies } from './EditFrequenciesContext';
import { AbbreviatedFrequenciesTable } from '../abbreviated/AbbreviatedFrequenciesTable';
import { Banner } from '~/shared/components/Banner';
import { useAbbreviateFrequencies } from '../abbreviated/useAbbreviateFrequencies';

export function EditFrequenciesCalendar() {
	const abbreviateFrequencies = useAbbreviateFrequencies();
	const {
		dispatch,
		wizardState: {
			draftEvaluations,
			draftRoutineFrequencies,
			existingFrequencies,
			certificationPeriod,
			discontinuedFrequencies,
			frequencyDiscontinueError
		}
	} = useEditFrequencies();

	const draftFrequencies = [
		...draftEvaluations.map(x => ({ ...x, frequencyTypeId: FrequencyTypeEnum.Evaluation, isReadOnly: false })),
		...draftRoutineFrequencies.map(x => ({
			...x,
			frequencyTypeId: FrequencyTypeEnum.Routine,
			frequencyStatusId: FrequencyStatusEnum.Drafted,
			isReadOnly: false
		}))
	];

	const draftFrequencyCalendarEvents = convertDraftFrequenciesToCalendarEvent(draftFrequencies);

	const existingFrequencyCalendarEvents = convertFrequenciesToCalendarEvent([...discontinuedFrequencies, ...existingFrequencies]);

	const onCalendartemClicked = (eventId: string) => {
		const draftFrequencyIds = draftFrequencies.map(x => x.id);
		if (draftFrequencyIds.includes(eventId)) {
			dispatch({ type: 'DRAFT_CALENDAR_EVENT_CLICKED', frequencyId: eventId });
		} else if (discontinuedFrequencies.map(x => x.id).includes(eventId)) {
			dispatch({ type: 'DRAFT_DISCONTINUED_CALENDAR_EVENT_CLICKED', frequencyId: eventId });
		} else {
			dispatch({ type: 'EXISTING_CALENDAR_EVENT_CLICKED', frequencyId: eventId });
		}
	};

	const abbreviatedFrequencies = abbreviateFrequencies(
		[
			...draftEvaluations.map(x => ({
				...x,
				frequencyTypeId: FrequencyTypeEnum.Evaluation,
				frequencyCadenceTypeId: FrequencyCadenceTypeEnum.Evaluation,
				frequencyStatusId: FrequencyStatusEnum.Drafted
			})),
			...draftRoutineFrequencies.map(x => ({
				...x,
				frequencyTypeId: FrequencyTypeEnum.Routine,
				frequencyStatusId: FrequencyStatusEnum.Drafted
			})),
			...existingFrequencies.filter(x => x.invariants.canBeDiscontinued)
		],
		certificationPeriod
	);

	return (
		<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
			<CertificationPeriodCalendar
				events={[...draftFrequencyCalendarEvents, ...existingFrequencyCalendarEvents]}
				certificationPeriod={certificationPeriod}
				onCalendarCellClicked={date => dispatch({ type: 'CALENDAR_CELL_CLICKED', selectedDate: date })}
				onCalendarItemClicked={event => onCalendartemClicked(event.id)}
			/>
			<div className="space-y-4">
				<AbbreviatedFrequenciesTable rows={abbreviatedFrequencies} />
				{frequencyDiscontinueError && (
					<Banner intent="warning" title="Nothing to order" message="">
						<p className="text-sm">{frequencyDiscontinueError}</p>
					</Banner>
				)}
			</div>
		</div>
	);
}
